import { NavLink, useLocation, useParams } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import styles from "./Header.module.css";
import Logo from "../../assets/Logo";
import ArrowFull from "../../assets/ArrowFull";
import LanguagePicker from "./LanguagePicker";

// HEADER COMPONENT
export default function Header({ isLight, openSearch }) {

    const { lang } = useParams();

    // MENU DATA AND TYPES

    const menuItems = [
        {
            id: "studios",
            label: "Studios",
            label_ko: "스튜디오",
            to: "/studios",
            children: [
                { id: "studios-gc", label: "Generative Visuals", label_ko: "AI 기반 비주얼 생성", to: "/studios/generative-visuals" },
                { id: "studios-stages", label: "Stages", label_ko: "스테이지", to: "/studios/stages" },
                { id: "alternative-formats", label: "Alternative Formats", label_ko: "대체 포맷", to: "/studios/alternative-formats" },
            ],
        },
        { id: "vfx", label: "Production", label_ko: "프로덕션", to: "/production" },
        { id: "labs", label: "Innovation", label_ko: "이노베이션", to: "/innovation" },
        {
            id: "about",
            label: "About",
            label_ko: "소개",
            to: "/about",
            children: [

                { id: "about-careers", label: "Careers", label_ko: "채용", to: "/about/careers" },
            ],
        },
        { id: "contact", label: "Contact", label_ko: "문의", to: "/contact" },
    ];

    const [isNavOpen, setIsNavOpen] = useState(false);
    const [activeSubmenuId, setActiveSubmenuId] = useState(null); // which submenu is open (mobile)

    const { pathname } = useLocation();
    const [colorClass, setColorClass] = useState(null);

    let menuClass = `${styles.menuBtn}`;
    let headerClass = `${styles.header}`;
    if (isLight) {
        headerClass += ` ${styles.light}`;
    }

    if (isNavOpen) {
        menuClass += ` ${styles.open}`;
        headerClass += ` ${styles.navOpen}`;

    }

    // iOS style header
    const [show, setShow] = useState(false);
    const [hide, setHide] = useState(false);
    const [lastScrollY, setLastScrollY] = useState(0);

    // close everything when route changes
    useEffect(() => {
        setIsNavOpen(false);
        setActiveSubmenuId(null);

        setColorClass(styles.none);

        if (pathname.startsWith("/en/studios") || pathname.startsWith("/ko/studios")) {
            setColorClass(styles.studios);
        }

        if (pathname.startsWith("/en/production") || pathname.startsWith("/en/projects") || pathname.startsWith("/ko/production") || pathname.startsWith("/ko/projects")) {
            setColorClass(styles.vfx);
        }

        if (pathname.startsWith("/en/innovation") || pathname.startsWith("/ko/innovation")) {
            setColorClass(styles.labs);
        }

        let ticking = false;

        const update = () => {
            const currentScrollY = window.scrollY;

            if (Math.abs(currentScrollY - lastScrollY) < 10) {
                ticking = false;
                return;
            }

            if (currentScrollY < 50) {

                setShow(false);

            } else {

                if (currentScrollY > lastScrollY) {
                    setShow(false);
                    setHide(true);
                } else {
                    setShow(true);
                    setHide(false);
                }

            }

            setLastScrollY(currentScrollY);
            ticking = false;
        };

        const handleScroll = () => {
            if (!ticking) {
                window.requestAnimationFrame(update);
                ticking = true;
            }
        }

        window.addEventListener("scroll", handleScroll, { passive: true });
        return () => window.removeEventListener("scroll", handleScroll);

    }, [pathname, lastScrollY]);

    const menuToggle = () => {
        setIsNavOpen(!isNavOpen);
    };

    const closeAll = () => {
        setIsNavOpen(false);
        setActiveSubmenuId(null);
    };

    return (
        <header className={`${show ? styles.show : ''} ${hide ? styles.hide : ''} ${headerClass}`}>

            <div className={`${styles.colors} ${colorClass}`}>
                <div className={styles.color}></div>
                <div className={styles.color}></div>
                <div className={styles.color}></div>
                <div className={styles.color}></div>
            </div>

            <NavLink to={`/${lang}`} className={styles.logo} onClick={closeAll}>
                <Logo />
            </NavLink>

            {/* Desktop nav (hidden on mobile via CSS) */}
            <DesktopNav
                items={menuItems}
                lang={lang}
            />

            <div className={styles.right}>
                <div className={styles.search_wrap} onClick={openSearch}>
                    <svg id="searchIcon" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M14 14L11.1 11.1M12.6667 7.33333C12.6667 10.2789 10.2789 12.6667 7.33333 12.6667C4.38781 12.6667 2 10.2789 2 7.33333C2 4.38781 4.38781 2 7.33333 2C10.2789 2 12.6667 4.38781 12.6667 7.33333Z" stroke="var(--global-background)" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </div>

                <LanguagePicker isLight={isLight} />

                {/* Mobile hamburger */}
                <div className={menuClass} onClick={menuToggle}>
                    <div>
                        <span className={styles.line}></span>
                        <span className={styles.line}></span>
                        <span className={styles.line}></span>
                    </div>
                </div>
            </div>



            {/* Mobile drawer (hidden on desktop via CSS) */}
            <MobileNav
                items={menuItems}
                isOpen={isNavOpen}
                activeSubmenuId={activeSubmenuId}
                setActiveSubmenuId={setActiveSubmenuId}
                closeAll={closeAll}
                lang={lang}
            />
        </header>

    );
}


// DESKTOP NAV (hover for submenu dropdowns)
// Unsure if this should be in a different component file, but keeping it here for now
function DesktopNav({ items, lang }) {
    return (
        <nav className={styles.desktopNav} aria-label="Primary">
            <ul className={styles.desktopList}>
                {items.map((item) => {
                    const hasChildren = !!item.children?.length;
                    return (
                        <li key={item.id} className={hasChildren ? styles.hasSubmenu : undefined}>
                            {item.to ? (
                                <NavLink to={`/${lang}${item.to}`} className={({ isActive }) => (isActive ? styles.active : "")}>
                                    {lang === "ko" ? item.label_ko : item.label}
                                </NavLink>
                            ) : (
                                <span>{item.label}</span>
                            )}

                            {hasChildren && (
                                <div className={styles.dropdown}>
                                    <ul className={styles.dropdownList}>
                                        {item.children && item.children.map((child) => (
                                            <li key={child.id}>
                                                <NavLink to={`/${lang}${child.to}`} className={({ isActive }) => (isActive ? styles.active : "")}>
                                                    {lang === "ko" ? child.label_ko : child.label}
                                                </NavLink>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </li>
                    );
                })}
            </ul>
        </nav>
    );
}

// MOBILE NAV (click for submenu dropdowns)
// Also unsure if this should be in a different component file, but keeping it here for now
function MobileNav({
    items,
    isOpen,
    activeSubmenuId,
    setActiveSubmenuId,
    closeAll,
    lang,
}) {

    const toggleSubmenu = (id) =>
        setActiveSubmenuId(activeSubmenuId === id ? null : id);

    return (
        <nav
            className={[styles.mobileNav, isOpen ? styles.mobileNavOpen : ""].join(" ")}
            aria-hidden={!isOpen}
            aria-label="Mobile"
        >
            <ul className={styles.mobileList}>
                {items.map((item) => {
                    const hasChildren = !!item.children?.length;
                    const isSubmenuOpen = activeSubmenuId === item.id;

                    return (
                        <li key={item.id} className={hasChildren ? styles.hasSubmenu : undefined}>
                            <div className={styles.parentRow}>
                                {item.to ? (
                                    <NavLink to={`/${lang}${item.to}`} onClick={closeAll}>
                                        {lang === "ko" ? item.label_ko : item.label}
                                    </NavLink>
                                ) : (
                                    <span>{item.label}</span>
                                )}

                                {hasChildren && (
                                    <button
                                        type="button"
                                        className={styles.toggle}
                                        aria-expanded={isSubmenuOpen}
                                        aria-controls={`submenu-${item.id}`}
                                        onClick={() => toggleSubmenu(item.id)}
                                    >
                                        <span className={isSubmenuOpen ? styles.rotated : ""}>
                                            <ArrowFull />
                                        </span>
                                        <span className={styles.srOnly}>Toggle {lang === "ko" ? item.label_ko : item.label} submenu</span>
                                    </button>
                                )}
                            </div>

                            {hasChildren && (
                                <ul
                                    id={`submenu-${item.id}`}
                                    className={[styles.submenu, isSubmenuOpen ? styles.submenuOpen : ""].join(" ")}
                                >
                                    {item.children && item.children.map((child) => (
                                        <li key={child.id}>
                                            <NavLink to={`/${lang}${child.to}`} onClick={closeAll}>
                                                {lang === "ko" ? child.label_ko : child.label}
                                            </NavLink>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </li>
                    );
                })}
            </ul>
        </nav>
    );

}